import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as _ from 'underscore';

import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { BrowserTitleService } from '../../../commonInfra/services/BrowserTitleService';
import { ISanitizer, sanitize } from '../../../commonInfra/utils/Sanitize';
import { Projects, ProjectsResource } from '../../../projects/resources/Projects';
import { ProjectDetailsDialogCtrl } from '../../../ts/controllers/ProjectDetailsDialogCtrl';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { CommonErrorCode } from '../../../ts/helpers/ErrorCodes';
import { Filter } from '../../../ts/helpers/Filter';
import { Omit } from '../../../ts/helpers/TypescriptHelpers';
import { IContentInfo } from '../../../ts/models/IContentInfo';
import { IProjectDetailInfo, IProjectInfo } from '../../../ts/models/IProjectInfo';
import { IFavoritableItemInfo } from '../../../ts/models/resourceInfoParts';
import { IProjectActions } from '../../../ts/ServerApi/types/IProjectActions';
import { ModalService } from '../../../ts/services/ModalService';
import { enterSpace } from '../../actions/spaces/EnterSpaceAction';
import { addContentFavorite, removeContentFavorite } from '../../actions/spaces/FavoritesActions';
import { openContentDetailDialog } from '../../actions/spaces/OpenContentDetailDialogActions';
import { requestSpaceContents } from '../../actions/spaces/RequestSpaceContentsActions';
import {
  addContentTypeToProjectHierarchy,
  populateContentBreadcrumbs
} from '../../content/breadcrumbs/BreadcrumbHelpers';
import { usePrevious } from '../../hooks';
import { SessionSiteSelectors } from '../../selectors/SessionInfoSelectors';
import { ServiceLocator } from '../../ServiceLocator';
import { AppState } from '../../state/AppState';
import { SpaceInfo } from '../../state/SpaceInfo';
import { MapDispatchToProps, MapStateToProps } from '../../utils/ConnectUtils';
import { DetailsDialogInAngularPlaceHelper } from '../DetailsDialog/AngularContentDetailsDialog';
import {
  ConnectedQueryParamUpdateProvider,
  QueryParamUpdateProviderOwnProps
} from '../RouterHelpers/QueryParamUpdateProvider';
import { RequestAccessError } from '~src/requestAccess/components/RequestAccessError';
import { RouterError } from '../RouterHelpers/RouterError';
import { ConnectedSpaceInfoProvider } from '../RouterHelpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { ConnectedProjectSpace } from './ConnectedProjectSpace';
import { ProjectSpaceOwnProps } from './ProjectSpace';


export interface ProjectSpaceProviderOwnProps extends ReactDirectiveProps {
  readonly routeLocationSearch: string;
  readonly siteName: string;
  readonly projectId: string;
  readonly urls: SpacesSiteUrls;
  readonly renderProjectSpace: (props: ProjectSpaceOwnProps) => JSX.Element;
  readonly setFetchedAllData: (isFetched: boolean) => void;
}

export interface ProjectSpaceProviderDispatchProps {
  readonly enterSpace: (spaceInfo: SpaceInfo) => void;
  readonly requestSpaceContents: () => void;
  readonly addContentFavorite: (item: IFavoritableItemInfo) => void;
  readonly removeContentFavorite: (item: IFavoritableItemInfo) => void;
  readonly openContentDetailDialog: (content: IContentInfo, availableActions: IProjectActions) => void;
}

export interface ProjectSpaceProviderStateProps {
  readonly isRequestAccessEnabled: boolean;
}

export interface ProjectSpaceProviderPropsFromAngular {
  readonly Projects: ProjectsResource;
  readonly sanitizer: ISanitizer;
  readonly modalService: ModalService;
  readonly q: ng.IQService;
}

export type ProjectSpaceNonAngularProps = ProjectSpaceProviderStateProps & ProjectSpaceProviderOwnProps & ProjectSpaceProviderDispatchProps;

export type ProjectSpaceProviderContainerProps =  ProjectSpaceProviderStateProps & ProjectSpaceProviderOwnProps &  ProjectSpaceProviderDispatchProps & ProjectSpaceProviderPropsFromAngular;

export interface ProjectSpaceProviderContainerState {
  readonly projectInfo: IProjectInfo;
  readonly projectDetail: IProjectDetailInfo;
  readonly initialRender: boolean;
  readonly renderChildren: boolean;
  readonly hasError: boolean;
  readonly projectActions: IProjectActions;
  readonly errorCode: number;
}

const InitialState: ProjectSpaceProviderContainerState = {
  projectInfo: undefined,
  projectDetail: undefined,
  initialRender: true,
  renderChildren: false,
  hasError: false,
  projectActions: undefined,
  errorCode: undefined
};

export class ProjectSpaceProviderContainer extends React.PureComponent<ProjectSpaceProviderContainerProps, ProjectSpaceProviderContainerState> {

  private detailsDialogHelper: DetailsDialogInAngularPlaceHelper;
  private unmounted: boolean = false;

  public constructor(props: ProjectSpaceProviderContainerProps){
    super(props);
    this.state =  InitialState;
  }

  public render() {
    if (this.state.renderChildren) {
      return this.props.renderProjectSpace({
        projectDetail: {
          usageInfo: this.state.projectInfo.usageInfo,
          usageInfoMaxValues: this.state.projectInfo.usageInfoMaxValues,
          ...this.state.projectDetail
        },
        projectId: this.state.projectDetail.id,
        openDetail: FeatureFlags.reactDetailsDialogs ? this.wrappedOpenContentDetailDialog : this.openDetail,
        addToFavorites: this.favorite,
        removeFromFavorites: this.unfavorite,
        sanitizer: this.props.sanitizer,
        angularContext: this.props.angularContext
      });

    } else if (this.state.hasError) {
      if (this.props.isRequestAccessEnabled) {
        let errorCode = this.state.errorCode;
        if (!errorCode && this.state.projectInfo === null && !_.isUndefined(this.state.projectDetail)) {
          // projectInfo is null because getProjects returned no project. projectDetail _did_ return a project (it's not undefined anymore),
          // which means the project exists, the user just does not have permissions to view it. Set the error to insufficient permissions
          // manually so the user can request access to the project. TFS954605
          errorCode = CommonErrorCode.CallingUserInsufficientPermissions;
        }
        return <RequestAccessError url={this.props.urls.exploreUrl()} contentIdentifier={this.props.projectId} contentType={'project'} errorCode={errorCode}/>;
      }
      return <RouterError errorType={'projects'} urls={this.props.urls} />;
    } else {
      return null;
    }
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  public componentDidMount() {
    this.getData();
  }

  public componentDidUpdate(prevProps: ProjectSpaceProviderContainerProps) {
    if(this.changedProject(prevProps)) {
      this.props.setFetchedAllData(false);
      this.updateState(InitialState /* want the initial state for the new project */);
      this.getData();
      return;
    }

    // if we dont have all we need - do nothing
    if (!this.haveAllDataNeededToRender()) {
      return;
    }

    if (this.state.initialRender) {
      this.props.enterSpace(
        {
          item: this.state.projectDetail,
          spaceType: 'project',
          subspaceName: undefined
        }
      );

      this.detailsDialogHelper = new DetailsDialogInAngularPlaceHelper(
        this.getData,
        this.props.q,
        this.props.angularContext,
        this.props.modalService,
      );

      this.props.requestSpaceContents();

      this.setState({initialRender: false, renderChildren: true});

      BrowserTitleService.setTitleWithPlaceEntityName({ placeEntityName: this.state.projectDetail.name });
    }
  }

  private getData = () => {
    this.getProjectInfo();
    this.getProjectDetail();
    this.getActions();
  }

  private wrappedOpenContentDetailDialog = () => {
    this.props.openContentDetailDialog(this.state.projectDetail, this.state.projectActions);
  }

  private openDetail = () => {
    const { projectActions, projectDetail, projectInfo } = this.state;
    this.detailsDialogHelper.openDialog(
      this.state.projectDetail,
      {
        controller: ProjectDetailsDialogCtrl,
        resolve: {
          projectActions: () => projectActions,
          projectDetailInfo: () => projectDetail,
          detailsDialogHelper: () => this.detailsDialogHelper
        }
      }
    );
  }

  private favorite = () => {
    this.props.addContentFavorite(this.state.projectDetail);
  }

  private unfavorite = () => {
    this.props.removeContentFavorite(this.state.projectDetail);
  }

  private getProjectInfo = async () => {
    try {
      const project = await this.props.Projects.all()
        .withFilters([Filter.fieldEq('id', this.props.projectId)])
        .getFirst();

      if (!project) {
        this.updateState({hasError: true});
      }

      this.updateState({projectInfo: project});
    } catch (e) {
      this.updateState({hasError: true, errorCode: e as number});
    }
  }

  private getProjectDetail = async () => {
    try {
      const detail = await this.props.Projects.getById(this.props.projectId);

      if (!detail) {
        this.updateState({hasError: true});
        return;
      }

      this.updateState({projectDetail: detail});
      const projectHierarchy = await this.props.Projects.getProjectHierarchy(detail.id);
      const projectHierarchyWithType = addContentTypeToProjectHierarchy(projectHierarchy);
      populateContentBreadcrumbs(projectHierarchyWithType, this.props.urls);
      this.props.setFetchedAllData(true);
    } catch (e) {
      this.updateState({hasError: true, errorCode: e as number});

    }
  }

  private getActions = async () => {
    try {
      const actions = await this.props.Projects.actions(this.props.projectId);
      this.updateState({projectActions: actions});
    } catch (e) {}
  }

  private haveAllDataNeededToRender() {
    return !_.isUndefined(this.state.projectInfo) &&
      !_.isUndefined(this.state.projectDetail) &&
      !this.state.hasError;
  }

  private changedProject(prevProps: ProjectSpaceProviderContainerProps) {
    return !_.isUndefined(this.props.siteName) &&
      !_.isUndefined(this.props.projectId) &&
      (prevProps.siteName !== this.props.siteName || prevProps.projectId !== this.props.projectId);
  }

  private updateState<K extends keyof ProjectSpaceProviderContainerState>(newState: Pick<ProjectSpaceProviderContainerState, K>): void {
    if (!this.unmounted) {
      this.setState(newState);
    }
  }
}

export namespace ProjectSpaceProviderAngularProvider {
  function getAngularServicesWithServiceLocator() {
    const modalService = ServiceLocator.get<ModalService>('ModalService');
    const q = ServiceLocator.get<ng.IQService>('$q');
    return {
      Projects: Projects,
      sanitizer: sanitize,
      modalService,
      q,
    };
  }

  function selectPropsFromAngularServices(angularServices: ProjectSpaceProviderPropsFromAngular) {
    return angularServices;
  }

  export const HOC = angularProviderHOC<ProjectSpaceNonAngularProps, ProjectSpaceProviderPropsFromAngular, ProjectSpaceProviderPropsFromAngular>({
    getAngularServicesWithServiceLocator,
    selectPropsFromAngularServices,
  });
}

const ProjectSpaceProviderAng: React.ComponentType<ProjectSpaceNonAngularProps> = ProjectSpaceProviderAngularProvider.HOC(ProjectSpaceProviderContainer);

const mapStateToProps: MapStateToProps<ProjectSpaceProviderStateProps> = (state: AppState) => ({
  isRequestAccessEnabled: SessionSiteSelectors.isRequestAccessEnabled(state)
});

const mapDispatchToProps: MapDispatchToProps<ProjectSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  requestSpaceContents,
  enterSpace,
  addContentFavorite,
  removeContentFavorite,
  openContentDetailDialog,
}, dispatch);

export const ConnectedProjectSpaceProviderContainer: React.ComponentClass<ProjectSpaceProviderOwnProps>
  = connect(mapStateToProps, mapDispatchToProps)(ProjectSpaceProviderAng);

const renderProjectSpace = (props: ProjectSpaceOwnProps): JSX.Element => <ConnectedProjectSpace {...props} />;

export interface LocationPathProp {
  readonly routeLocationPath: string;
}

export type ProjectSpaceProviderProps = LocationPathProp & Omit<ProjectSpaceProviderOwnProps, 'renderProjectSpace' | 'setFetchedAllData'>;

export interface ProjectSpaceProviderState {
  readonly fetchedAllData: boolean;
}

export const ProjectSpaceProvider: React.FunctionComponent<ProjectSpaceProviderProps> = React.memo(props => {

  const [fetchedAllData, setFetchedAllData] = React.useState(false);
  const previousProjectId = usePrevious(props.projectId);

  // TFSID:983648.  Handle case when updating this component with a new projectId
  const resetStore = !_.isUndefined(previousProjectId) && !_.isUndefined(props.projectId)
    && previousProjectId !== props.projectId;

  const queryUpdaterProps: QueryParamUpdateProviderOwnProps = {
    routeLocationPath: props.routeLocationPath,
    routeLocationSearch: props.routeLocationSearch,
    shouldUpdateContentTypes: true,
    shouldUpdateFilters: true,
    shouldUpdateSort: true,
    shouldUpdateBreadcrumbs: fetchedAllData,
    resetStore: resetStore
  };

  const ProjectSpaceInfo: SpaceInfo = {
    item: null,
    spaceType: 'project',
    subspaceName: undefined
  };

  return (
    <ConnectedSpaceInfoProvider spaceInfo={ProjectSpaceInfo}>
      <ConnectedQueryParamUpdateProvider {...queryUpdaterProps}>
        <ConnectedProjectSpaceProviderContainer {...{...props, renderProjectSpace, setFetchedAllData: setFetchedAllData}}/>
      </ConnectedQueryParamUpdateProvider>
    </ConnectedSpaceInfoProvider>
  );
});
